import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'

import { useProductService } from '@/services/product.service'
import { minutesToHHmm } from '@/lib/utils'

import * as dataGridColumn from '@/composables/data-grid-column'

export const useColumns = (supplierBrand = {}, keepDescription, currency, documentRowType, supersessionId = {}, documentRowProductPurchaseFamily = {}, includesVat = {}, productUnitOfMeasure = {}, currentCustomer = {}, laborPriceContext = {}) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setTotalVatValue, setTotalNoVatValue, setPriceVatValue } = useProductService()
  const { useRoundedTotalColumn, useRoundingColumn, useRoundingPercentageColumn, useRoundingDocumentTotalVatColumn } = dataGridColumn.useRoundingExpenseColumn(documentRowProductPurchaseFamily, includesVat)

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '6rem',
      visibleIndex: 1,
      fixed: false
    },
    dataGridColumn.useRowNumberColumn(),
    dataGridColumn.useDocumentRowTypeColumn({
      editCellTemplate: 'documentRowType',
      cellTemplate: 'documentRowTypeCellTemplate',
      productUnitOfMeasure: productUnitOfMeasure,
    }),
    dataGridColumn.useProcessingTypeColumn(),
    dataGridColumn.useCostColumn({validationRules: []}),
    dataGridColumn.useCostValorizationColumn({ priceContext: laborPriceContext }),
    webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) ? {
      dataField: 'worksheet.row_number',
      caption: i18n.t('common.worksheetRow'),
      width: '8rem',
      editCellTemplate: 'worksheetRowRef',
      cellTemplate: 'worksheetRowRef',
      alignment: 'left'
    } : {},
    dataGridColumn.useSupplierBrandColumn({ supplierBrand, documentRowType }),
    ...dataGridColumn.useProductColumns({ supplierBrand, keepDescription, customer: currentCustomer, supersessionId, productUnitOfMeasure, priceContext: laborPriceContext }),
    dataGridColumn.useDescriptionColumn({ validationRules: [{ type: 'required' }] }),
    dataGridColumn.useIsMovementColumn(),
    dataGridColumn.useQuantityColumn(),
    dataGridColumn.usePriceColumn({ dataField: 'product_price', caption: i18n.t('common.listing'), currency, validationRules: [], visible: false, showInColumnChooser: false }),
    dataGridColumn.usePriceColumn({ caption: i18n.t('common.unitPriceNoVat'), currency }),
    dataGridColumn.usePriceColumn({
      dataField: 'price_vat',
      caption: i18n.t('common.unitPriceVat'),
      currency,
      setCellValue: (newData, value, currentRowData) => {
        setPriceVatValue(newData, value, currentRowData)
      }
    }),
    dataGridColumn.useDiscountCategoryColumn(),
    ...dataGridColumn.useVatTypeColumns({
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE || type === webSiteStore.documentRowTypesEnum.LABOR || type === webSiteStore.documentRowTypesEnum.EXPENSE || type === webSiteStore.documentRowTypesEnum.SERVICE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useTotalNoVatColumn({ currency, setCellValue: (newData, value, currentRowData) => { setTotalNoVatValue(newData, value, currentRowData) } }),
    dataGridColumn.useTotalVatColumn({ currency, setCellValue: (newData, value, currentRowData) => { setTotalVatValue(newData, value, currentRowData, includesVat.value) } }),
    dataGridColumn.useProcessingColumn(),
    dataGridColumn.useProcessingParentColumn(),
    dataGridColumn.useMechanicsColumn(),
    dataGridColumn.useNotesColumn(),
    dataGridColumn.useDiscountColumn({includesVat: includesVat.value }),
    dataGridColumn.useDiscount2Column({includesVat: includesVat.value }),
    dataGridColumn.useDiscount3Column({includesVat: includesVat.value }),
    {
      dataField: 'total_discount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useChargePercentageColumn({ caption: i18n.t('common.chargePercentageAlt') }),
    dataGridColumn.useUrgencyDiscountColumn(),
    dataGridColumn.useUrgencyDiscount2Column(),
    dataGridColumn.useUnitOfMeasureColumn({
      showClearButton: false,
      productUnitOfMeasure: productUnitOfMeasure,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useDepositColumn({ visible: false, showInColumnChooser: false, validationRules: [] }),
    dataGridColumn.useDepositColumn({
      dataField: 'garage',
      caption: i18n.t('common.garage'),
      visible: false,
      showInColumnChooser: false,
      validationRules: []}
    ),
    {
      caption: i18n.t('common.timeEquivalent'),
      dataField: 'timeEquivalent',
      editCellTemplate: 'timeEquivalentEditor',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.actualDuration'),
      dataField: 'actual_duration',
      dataType: 'number',
      width: '5rem',
      format: minutesToHHmm,
      calculateDisplayValue: rowData => rowData.partial_actual_duration ?? rowData.actual_duration,
      visible: false,
      showInColumnChooser: false
    },
    {
      caption: i18n.t('common.suggestedTime'),
      dataField: 'suggested_time',
      dataType: 'number',
      format: minutesToHHmm,
      calculateDisplayValue: rowData => rowData.partial_suggested_time ?? rowData.suggested_time,
      editorOptions: { readOnly: true, format: minutesToHHmm },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.operationCode'),
      dataField: 'operationCode',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.recognizedTime'),
      dataField: 'recognizedTime',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    useRoundedTotalColumn({ currency }),
    useRoundingColumn({ currency }),
    useRoundingPercentageColumn(),
    useRoundingDocumentTotalVatColumn({ currency }),
  ]
}
